import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { notifyAfterLock } from "../afterLock/saleOutwardAfterLock";
import { checkFifo, saveSaleOutward } from "../api/saleOutwardApi";
import type { SaleKindConfig } from "../config/saleKinds";
import { confirmFifo, confirmMissingExchangeSerials, confirmVariance, showBlockingErrors } from "../components/dialogs";
import type { SaleOutwardAction, SaleOutwardState } from "../state/saleOutwardReducer";
import { getVarianceItems } from "../state/selectors";
import type { CreateSaleOutwardRequest, SaleOrderLineView, SaleOutwardError, SubmitStatus } from "../types";
import { validateSaleOutward } from "../validation/validateSaleOutward";

/** Dựng body gửi lên create-sale-order-out-ward, cùng cấu trúc trang cũ đang gửi */
export const buildSaveRequest = (state: SaleOutwardState, status: SubmitStatus): CreateSaleOutwardRequest => {
  const { header, order, rows, transactionId } = state;
  return {
    sourceId: 0,
    tblItemTransactionHeaderCommand: {
      ...header,
      status,
      branchId: order.fromBranchId ?? null,
      subInvId: order.fromInvId ?? null,
      transactionId: transactionId || header.transactionId,
      attribute11: header.attribute11?.toString() ?? "",
      attribute12: header.attribute12?.toString() ?? "",
      attribute4: order.fromBranchName ?? null,
      // Cả xuất bán và đổi trả đều ghi khách hàng (trang cũ ghi tên chi nhánh đích cho đổi trả là sai)
      attribute5: `${order.cusCode ?? ""} - ${order.cusName ?? ""}`,
      ecommerceOrderId: order.ecommerceOrderId ?? null,
    },
    serials: rows.map((row) => ({
      primaryQuantity: row.primaryQuantity,
      subQuantity: row.subQuantity,
      serial: row.serial,
      invId: order.fromInvId ?? row.invId,
      note: row.note,
      locatorId: Number(row.locatorId) || Number(row.positionId) || null,
      linkedDeviceSerial: row.linkedDeviceSerial ?? null,
      // Xuất đổi trả: "Serial nhập" đã chọn; rỗng thì backend tự ghép theo dòng đơn
      exchangeOldSerials: row.exchangeOldSerials?.length ? row.exchangeOldSerials : null,
    })),
    isLock: status === "LOCK",
  };
};

/** Kho xuất có mã HB (hàng bán) hoặc SR (showroom) thì kiểm tra FIFO trước khi lưu */
const shouldCheckFifo = (fromInvCode: string | null | undefined) =>
  !!fromInvCode && (fromInvCode.includes("HB") || fromInvCode.includes("SR"));

export interface UseSaleOutwardSubmitArgs {
  state: SaleOutwardState;
  dispatch: React.Dispatch<SaleOutwardAction>;
  lines: SaleOrderLineView[];
  kind: SaleKindConfig;
  readOnly: boolean;
  /** Bấm "Tới dòng đơn" / "Tới dòng" / "Chọn thiết bị" trong hộp lỗi chặn */
  onGoToIssue?: (error: SaleOutwardError) => void;
}

/**
 * Lưu tạm và hoàn thành phiếu. Nút bấm và phím tắt Ctrl+S / Ctrl+Q cùng gọi `submit`,
 * nên cùng qua một bước kiểm tra (trang cũ để phím tắt bỏ qua điều kiện disable của nút).
 * Có cờ chống bấm hai lần; lưu lỗi thì trạng thái phiếu không đổi (trang cũ bị kẹt ở LOCK).
 */
export const useSaleOutwardSubmit = ({
  state,
  dispatch,
  lines,
  kind,
  readOnly,
  onGoToIssue,
}: UseSaleOutwardSubmitArgs) => {
  const busyRef = React.useRef(false);
  const [submitting, setSubmitting] = React.useState<SubmitStatus | null>(null);
  const [lastLockedId, setLastLockedId] = React.useState<number | null>(null);

  const latest = React.useRef({ state, lines, kind, readOnly, onGoToIssue });
  latest.current = { state, lines, kind, readOnly, onGoToIssue };

  const submit = React.useCallback(
    async (status: SubmitStatus) => {
      if (busyRef.current) return;
      const { state, lines, kind, readOnly, onGoToIssue } = latest.current;

      const errors = validateSaleOutward({ status, header: state.header, rows: state.rows, lines, kind, readOnly });
      if (!state.order.fromInvId) {
        errors.push({ code: "NO_WAREHOUSE", message: "Không xác định được kho xuất của chứng từ." });
      }
      if (errors.length) {
        showBlockingErrors(
          status === "LOCK" ? "Chưa thể hoàn thành phiếu" : "Chưa thể lưu phiếu",
          errors,
          onGoToIssue,
        );
        return;
      }

      busyRef.current = true;
      setSubmitting(status);
      try {
        const variance = getVarianceItems(lines);
        if (variance.length && !(await confirmVariance(variance, status === "LOCK"))) return;

        // Xuất đổi trả: đơn có nhiều serial trả mà còn serial xuất chưa chọn "Serial nhập" thì hỏi, không chặn
        if (status === "LOCK" && kind.exchangeSerialColumn && (state.order.returnSerials?.length ?? 0) > 1) {
          const missing = state.rows
            .filter((row) => !row.isNoSerial && !(row.exchangeOldSerials?.length))
            .map((row) => String(row.serial ?? "").trim())
            .filter(Boolean);
          if (missing.length && !(await confirmMissingExchangeSerials(missing))) return;
        }

        const request: CreateSaleOutwardRequest = {
          ...buildSaveRequest(state, status),
          // Xuất bán: nhờ backend tạo đơn vận chuyển và gửi ZNS sau khi khoá (cùng API với trang cũ, trang cũ không gửi cờ này)
          dispatchSaleOrder: status === "LOCK" && (kind.createShippingOrders || kind.sendZns),
        };
        if (shouldCheckFifo(state.order.fromInvCode)) {
          try {
            const fifo = await checkFifo(
              request.serials.map((s) => s.serial ?? ""),
              request.tblItemTransactionHeaderCommand.branchId ?? null,
              request.tblItemTransactionHeaderCommand.subInvId ?? null,
            );
            if (fifo && fifo.success === false && !(await confirmFifo(fifo.data ?? []))) return;
          } catch (error) {
            console.error("[SaleOutward] kiểm tra FIFO lỗi", error);
            NotificationExtension.Warn("Không kiểm tra được FIFO, phiếu vẫn được lưu.");
          }
        }

        const result = await saveSaleOutward(request, status);
        if (!result.ok) {
          NotificationExtension.Fails(
            result.message || (status === "LOCK" ? "Khoá phiếu xuất kho thất bại" : "Lưu phiếu xuất kho thất bại"),
          );
          return;
        }

        if (status === "OPEN") {
          dispatch({ type: "SAVED", transactionId: result.transactionId });
          NotificationExtension.Success("Lưu phiếu xuất kho thành công");
          return;
        }

        const lockedId = result.transactionId || state.transactionId;
        dispatch({ type: "LOCKED", transactionId: lockedId, finishDate: new Date().toString() });
        setLastLockedId(lockedId || null);

        // Tạo đơn vận chuyển và gửi ZNS do backend làm nền sau khi khoá, ở đây chỉ báo người dùng
        notifyAfterLock(kind);
      } catch (error) {
        console.error("[SaleOutward] lưu phiếu lỗi", error);
        NotificationExtension.Fails("Có lỗi khi lưu phiếu xuất kho, vui lòng thử lại.");
      } finally {
        busyRef.current = false;
        setSubmitting(null);
      }
    },
    [dispatch],
  );

  return { submit, submitting, lastLockedId };
};
